// Vector pieces of the results view: body system icons, status icons, the
// body systems wheel, half-circle score gauges, score bars and z-score bars.
// Colours come from CSS classes so both themes can restyle them.
import React from 'react';
import {
  faCircleCheck,
  faCircleExclamation,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import { ICONS } from './bodySystems';
import { FLAG_Z } from './reportModel';
import { STATUS_LABEL } from '../copy';
import './proteorisk-report.css';

function Shape({ shape }) {
  const transform = shape.transform || undefined;
  if (shape.d) return <path d={shape.d} transform={transform} />;
  return (
    <circle
      cx={shape.cx}
      cy={shape.cy}
      r={shape.r}
      transform={transform}
      fill={shape.fill ? 'currentColor' : 'none'}
    />
  );
}

// The icon's shapes in its own 24 x 24 box, for use inside another <svg>.
export function IconShapes({ code }) {
  const shapes = ICONS[code];
  if (!shapes) throw new Error(`No icon is drawn for ${code}`);
  return (
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {shapes.map((shape, index) => <Shape key={index} shape={shape} />)}
    </g>
  );
}

export function SystemIcon({ code, size = 24, className = '' }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <IconShapes code={code} />
    </svg>
  );
}

const STATUS_ICONS = Object.freeze({
  typical: faCircleCheck,
  moderate: faTriangleExclamation,
  high: faCircleExclamation,
});

function faPath(definition) {
  const [width, height, , , path] = definition.icon;
  return { width, height, path: Array.isArray(path) ? path.join(' ') : path };
}

// Check for typical, warning for a moderate pattern, danger for a strong one.
export function StatusIcon({ status, size = 14, className = '' }) {
  const { width, height, path } = faPath(STATUS_ICONS[status]);
  return (
    <svg
      className={`PROTEORISK-status-icon ${status} ${className}`}
      width={size}
      height={size}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

// Inline version of the status icon for use inside the wheel's <svg>.
function StatusGlyph({ status, x, y, size }) {
  const { width, height, path } = faPath(STATUS_ICONS[status]);
  const scale = size / Math.max(width, height);
  return (
    <g className={`PROTEORISK-status-icon ${status}`} transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d={path} fill="currentColor" />
    </g>
  );
}

function polar(cx, cy, radius, degrees) {
  const radians = (degrees * Math.PI) / 180;
  return [cx + radius * Math.sin(radians), cy - radius * Math.cos(radians)];
}

// An annular sector between two clock angles (degrees clockwise from twelve).
function sectorPath(cx, cy, inner, outer, from, to) {
  const [ox0, oy0] = polar(cx, cy, outer, from);
  const [ox1, oy1] = polar(cx, cy, outer, to);
  const [ix1, iy1] = polar(cx, cy, inner, to);
  const [ix0, iy0] = polar(cx, cy, inner, from);
  const large = to - from > 180 ? 1 : 0;
  return `M ${ox0} ${oy0} A ${outer} ${outer} 0 ${large} 1 ${ox1} ${oy1} `
    + `L ${ix1} ${iy1} A ${inner} ${inner} 0 ${large} 0 ${ix0} ${iy0} Z`;
}

// Must match .PROTEORISK-wheel-status in proteorisk-report.css.
const STATUS_FONT = '600 11.5px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
let measureContext = null;

function textWidth(value, font) {
  if (!measureContext) measureContext = document.createElement('canvas').getContext('2d');
  measureContext.font = font;
  return measureContext.measureText(value).width;
}

const WHEEL = Object.freeze({
  width: 680, height: 470, cx: 340, cy: 235, outer: 134, inner: 82, label: 156, icon: 26, gap: 1.4,
});

function WheelLabel({ system, angle, onSelect }) {
  const [x, y] = polar(WHEEL.cx, WHEEL.cy, WHEEL.label, angle);
  const sin = Math.sin((angle * Math.PI) / 180);
  const cos = Math.cos((angle * Math.PI) / 180);
  let anchor = 'middle';
  let left = x;
  if (sin > 0.3) { anchor = 'start'; left = x + 6; }
  if (sin < -0.3) { anchor = 'end'; left = x - 6; }
  const lineHeight = 16;
  const height = system.lines.length * lineHeight + 18;
  let top = y - height / 2;
  if (cos > 0.85) top = y - height;
  if (cos < -0.85) top = y + 2;
  const status = STATUS_LABEL[system.status];
  // The status line is "icon, gap, words"; its width places the icon.
  const iconSize = 12;
  const gap = 5;
  const lineWidth = iconSize + gap + textWidth(status, STATUS_FONT);
  let lineLeft = left;
  if (anchor === 'end') lineLeft = left - lineWidth;
  if (anchor === 'middle') lineLeft = left - lineWidth / 2;
  return (
    <g className="PROTEORISK-wheel-label" onClick={() => onSelect(system.code)}>
      {system.lines.map((line, index) => (
        <text
          key={line}
          x={left}
          y={top + 12 + index * lineHeight}
          textAnchor={anchor}
          className="PROTEORISK-wheel-name"
        >
          {line}
        </text>
      ))}
      <StatusGlyph
        status={system.status}
        x={lineLeft}
        y={top + system.lines.length * lineHeight + 3}
        size={iconSize}
      />
      <text
        x={lineLeft + iconSize + gap}
        y={top + system.lines.length * lineHeight + 13}
        textAnchor="start"
        className={`PROTEORISK-wheel-status ${system.status}`}
      >
        {status}
      </text>
    </g>
  );
}

export function BodySystemWheel({ systems, typical, selected, onSelect }) {
  const slice = 360 / systems.length;
  const select = (code) => onSelect(selected === code ? null : code);
  return (
    <svg
      className={`PROTEORISK-wheel ${selected ? 'has-selection' : ''}`}
      viewBox={`0 0 ${WHEEL.width} ${WHEEL.height}`}
      role="group"
      aria-label="Body systems"
    >
      <circle className="PROTEORISK-wheel-rim" cx={WHEEL.cx} cy={WHEEL.cy} r={WHEEL.outer + 7} />
      {systems.map((system, index) => {
        const angle = index * slice;
        const from = angle - slice / 2 + WHEEL.gap;
        const to = angle + slice / 2 - WHEEL.gap;
        const [ix, iy] = polar(WHEEL.cx, WHEEL.cy, (WHEEL.inner + WHEEL.outer) / 2, angle);
        const label = `${system.name}: ${STATUS_LABEL[system.status]}`;
        return (
          <g
            key={system.code}
            className={`PROTEORISK-wheel-segment ${system.status} ${selected === system.code ? 'selected' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={label}
            aria-pressed={selected === system.code}
            onClick={() => select(system.code)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                select(system.code);
              }
            }}
          >
            <title>{label}</title>
            <path className={`PROTEORISK-seg ${system.status}`} d={sectorPath(WHEEL.cx, WHEEL.cy, WHEEL.inner, WHEEL.outer, from, to)} />
            <g
              className={`PROTEORISK-seg-icon ${system.status}`}
              transform={`translate(${ix - WHEEL.icon / 2} ${iy - WHEEL.icon / 2}) scale(${WHEEL.icon / 24})`}
            >
              <IconShapes code={system.code} />
            </g>
            <WheelLabel system={system} angle={angle} onSelect={select} />
          </g>
        );
      })}
      <text x={WHEEL.cx} y={WHEEL.cy + 4} textAnchor="middle" className="PROTEORISK-wheel-count">
        {typical} of {systems.length}
      </text>
      <text x={WHEEL.cx} y={WHEEL.cy + 24} textAnchor="middle" className="PROTEORISK-wheel-caption">
        body systems
      </text>
      <text x={WHEEL.cx} y={WHEEL.cy + 38} textAnchor="middle" className="PROTEORISK-wheel-caption">
        look typical
      </text>
    </svg>
  );
}

// A small wheel without labels, for illustrations on the landing page.
export function MiniWheel({ systems, className = '' }) {
  const size = 200;
  const c = size / 2;
  const outer = 94;
  const inner = 58;
  const slice = 360 / systems.length;
  const typical = systems.filter((system) => system.status === 'typical').length;
  return (
    <svg className={`PROTEORISK-miniwheel ${className}`} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
      {systems.map((system, index) => {
        const angle = index * slice;
        const [ix, iy] = polar(c, c, (inner + outer) / 2, angle);
        return (
          <g key={system.code}>
            <path className={`PROTEORISK-seg ${system.status}`} d={sectorPath(c, c, inner, outer, angle - slice / 2 + 1.6, angle + slice / 2 - 1.6)} />
            <g className={`PROTEORISK-seg-icon ${system.status}`} transform={`translate(${ix - 9} ${iy - 9}) scale(${18 / 24})`}>
              <IconShapes code={system.code} />
            </g>
          </g>
        );
      })}
      <text x={c} y={c + 3} textAnchor="middle" className="PROTEORISK-wheel-count mini">{`${typical} of ${systems.length}`}</text>
      <text x={c} y={c + 19} textAnchor="middle" className="PROTEORISK-wheel-caption mini">look typical</text>
    </svg>
  );
}

// A half-circle gauge showing the score from 0 to 100.
export function ScoreGauge({ score100, tier, size = 76 }) {
  const cx = 40;
  const cy = 42;
  const radius = 32;
  const arc = (from, to) => {
    const [x0, y0] = polar(cx, cy, radius, from);
    const [x1, y1] = polar(cx, cy, radius, to);
    return `M ${x0} ${y0} A ${radius} ${radius} 0 ${to - from > 180 ? 1 : 0} 1 ${x1} ${y1}`;
  };
  const sweep = Math.max(3, 180 * Math.min(1, Math.max(0, score100 / 100)));
  return (
    <svg
      className={`PROTEORISK-gauge ${tier}`}
      width={size}
      height={size * 0.62}
      viewBox="0 0 80 50"
      role="img"
      aria-label={`Score ${score100} out of 100`}
    >
      <path className="PROTEORISK-gauge-track" d={arc(270, 450)} />
      <path className={`PROTEORISK-gauge-value ${tier}`} d={arc(270, 270 + sweep)} />
      <text x={cx} y={cy - 2} textAnchor="middle" className="PROTEORISK-gauge-number">{score100}</text>
    </svg>
  );
}

export function ScoreBar({ score100, tier }) {
  const position = Math.min(100, Math.max(0, score100));
  return (
    <div className={`PROTEORISK-scorebar ${tier}`} aria-hidden="true">
      <div className="PROTEORISK-scorebar-fill" style={{ width: `${position}%` }} />
      <div className="PROTEORISK-scorebar-dot" style={{ left: `${position}%` }} />
    </div>
  );
}

// Protein z-scores on a -4..4 scale with the typical band from -1.5 to 1.5,
// drawn with CSS so the dot stays round at any width.
const Z_LIMIT = 4;

function zPercent(z) {
  const clamped = Math.max(-Z_LIMIT, Math.min(Z_LIMIT, z));
  return ((clamped + Z_LIMIT) / (2 * Z_LIMIT)) * 100;
}

export function ZAxis() {
  return (
    <div className="PROTEORISK-zaxis" aria-hidden="true">
      <span style={{ left: `${zPercent(-FLAG_Z)}%` }}>-1.5</span>
      <span style={{ left: `${zPercent(0)}%` }}>0</span>
      <span style={{ left: `${zPercent(FLAG_Z)}%` }}>+1.5</span>
    </div>
  );
}

export function ZScoreBar({ z, flagged }) {
  const beyond = Math.abs(z) > Z_LIMIT;
  const bandLeft = zPercent(-FLAG_Z);
  return (
    <div className={`PROTEORISK-zbar ${flagged ? 'flagged' : ''}`} aria-hidden="true">
      <div className="PROTEORISK-zbar-track" />
      <div className="PROTEORISK-zbar-band" style={{ left: `${bandLeft}%`, width: `${zPercent(FLAG_Z) - bandLeft}%` }} />
      <div className="PROTEORISK-zbar-zero" style={{ left: `${zPercent(0)}%` }} />
      {beyond && <div className={`PROTEORISK-zbar-beyond ${z > 0 ? 'right' : 'left'}`} />}
      <div className="PROTEORISK-zbar-dot" style={{ left: `${zPercent(z)}%` }} />
    </div>
  );
}
